import React, { useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

import { EditOrganizationDetailsModalProps } from '../OverviewDetails.types'

import { useMutation, useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { Modal, AcknowledgeModal, ActionModal, AvatarInput, FormField, Spacer, TextInput } from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'

export function EditFilesModal({ open, onClose, refresh }: EditOrganizationDetailsModalProps) {
  const { t } = useTranslation()
  const { id } = useRequiredParams()
  const { adminService } = useServices()
  const [errorDialogState, setErrorDialogState] = useState(false)

  const [{ isLoading, success }, { apiFunc }] = useMutation(adminService.uploadOrganisationFile, undefined, () => {
    setErrorDialogState(true)
  })

  const initialValues = {
    file: null,
    description: ''
  }

  // TODO: Possibly change to react-hook-form
  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues,
    validationSchema: () =>
      Yup.object().shape({
        file: Yup.string().nullable(),
        description: Yup.string()
          .min(1, t('errorMessage.orgFieldCharacterLimit', { fieldName: t('typography.description') }))
          .max(255, t('errorMessage.orgFieldCharacterLimit', { fieldName: t('typography.description') }))
          .trim()
          .nullable()
      }),
    onSubmit: async ({ file, description }) => {
      if (!file) {
        setFieldError('file', t('errorMessage.required'))

        return
      }
      const formData = new FormData()
      formData.append('file', file)
      formData.append('description', description)

      apiFunc({
        payload: { formData },
        id
      })
    }
  })

  const {
    values,
    setFieldValue,
    setFieldError,
    dirty,
    handleChange,
    handleBlur,
    handleSubmit,
    isValid,
    resetForm,
    errors,
    touched
  } = formik

  const handleCloseModal = () => {
    onClose()
    refresh()
    resetForm()
  }

  const handleOnErrorClose = () => {
    setErrorDialogState(false)
  }

  const successOrErrorModal =
    success && !errorDialogState ? (
      <AcknowledgeModal
        result="positive"
        title={t('dialog.editOrganizationDetails.success.title', { organizationCard: 'Files' })}
        description={t('dialog.editOrganizationDetails.success.description', { organizationCard: 'files' })}
        buttonText={t('button.close')}
        handleClose={handleCloseModal}
      />
    ) : (
      <AcknowledgeModal
        result="negative"
        title={t('dialog.editOrganizationDetails.error.title', { organizationCard: 'Files' })}
        description={t('dialog.editOrganizationDetails.error.description', { organizationCard: 'files' })}
        buttonText={t('button.close')}
        handleClose={handleOnErrorClose}
      />
    )

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="edit-files-form-modal">
      {!success && !errorDialogState ? (
        <ActionModal
          title={t('typography.editFiles')}
          primaryButtonText={t('button.saveChanges')}
          secondaryButtonText={t('button.cancel')}
          type="submit"
          variant={isValid && dirty ? 'primary' : 'secondary'}
          disabled={!(isValid && dirty && !isLoading)}
          loading={isLoading}
          handleClose={() => {
            onClose()
            resetForm()
          }}
          handleSubmit={handleSubmit}
          primaryBtnTestId="files-details-submit-button"
          secondaryBtnTestId="files-details-cancel-button"
        >
          <form onSubmit={handleSubmit}>
            <FormikProvider value={formik}>
              <>
                <AvatarInput
                  altText="organization file"
                  image={values.file}
                  label={t('typography.file')}
                  buttonTitle={t('typography.chooseImage')}
                  onChange={file => {
                    setFieldValue('file', file)
                  }}
                  description={t('typography.fileDescriptionText')}
                  errorMessage={errors.file || ''}
                  avatarSize="large"
                  dataTestId="organization-images-file-input"
                />
                <Spacer size={4} axis="vertical" />

                <Spacer size={4} axis="vertical" />
                <FormField
                  label={t('typography.description')}
                  data-testid="description"
                  status={touched.description && errors.description ? 'danger' : 'default'}
                  statusMessage={errors.description || ''}
                  necessity="optional"
                >
                  <TextInput
                    name="description"
                    value={values?.description}
                    placeholder="Add description"
                    onBlur={handleBlur}
                    maxLength={255}
                    onChange={handleChange}
                  />
                </FormField>
              </>
            </FormikProvider>
          </form>
        </ActionModal>
      ) : (
        successOrErrorModal
      )}
    </Modal>
  )
}
